import { defineStore } from "pinia";
import axios from "axios";
import { compareInspectionDate, IInspection } from "@/types/inspection";
import { inspectionsListUrl, inspectionsSendMailUrl, syncInspectionsUrl } from "@/services/api";
import { ICostumer } from "@/types/costumer";
import { IIndoorAirCaptureRangeLimit, IIndoorFloorCaptureLimit, TrapType } from "@/types/trap";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/vue";

import {
    IIndoorFloorTrapAudit,
    IIndoorFlyingTrapAudit,
    IndoorFlyingTrapAuditQuantity,
    IOutdoorTrapAudit,
    OutdoorTrapAuditQuantity,
} from "@/types/audit";
import { useTrapsStore } from "@/stores/traps";
import { useNetworkStore } from "@/stores/network";

// You can name the return value of `defineStore()` anything you want,
// but it's best to use the name of the store and surround it with `use`
// and `Store` (e.g. `useUserStore`, `useCartStore`, `useProductStore`)
// the first argument is a unique id of the store across your application
export const useInspectionsStore = defineStore("inspections", {
    state: () => ({
        loading: false as boolean,
        remoteInspections: [] as IInspection[],
        localInspections: [] as IInspection[],
    }),
    persist: true,
    getters: {
        isLoading: (state) => state.loading,
        inspections: (state) => state.remoteInspections.concat(state.localInspections).sort(compareInspectionDate),
        getInspectionsByCostumerId:
            () =>
            (customerId: number): IInspection[] => {
                const store = useInspectionsStore();
                return store.inspections.filter((inspection) => inspection.costumer == customerId);
            },
        getInspectionById:
            () =>
            (id: number): IInspection | undefined => {
                const store = useInspectionsStore();
                return store.inspections.find((inspection) => inspection.id == id);
            },
        getInspectionOutdoorAudits: (state) => (inspectionId: number) => {
            const inspectionStore = useInspectionsStore();
            let index = state.localInspections.findIndex((localInspection) => inspectionId === localInspection.id);
            if (index < 0) {
                index = state.remoteInspections.findIndex((localInspection) => inspectionId === localInspection.id);
                if (index >= 0) {
                    return state.remoteInspections[index].outdoor_audits;
                } else {
                    return undefined;
                }
            } else {
                return state.localInspections[index].outdoor_audits;
            }
        },
        getInspectionIndoorFloorAudits: (state) => (inspectionId: number) => {
            const inspectionStore = useInspectionsStore();
            let index = state.localInspections.findIndex((localInspection) => inspectionId === localInspection.id);
            if (index < 0) {
                index = state.remoteInspections.findIndex((localInspection) => inspectionId === localInspection.id);
                if (index >= 0) {
                    return state.remoteInspections[index].indoor_floor_audits;
                } else {
                    return undefined;
                }
            } else {
                return state.localInspections[index].outdoor_audits;
            }
        },
        getInspectionIndoorFlyingAudits: (state) => (inspectionId: number) => {
            const inspectionStore = useInspectionsStore();
            let index = state.localInspections.findIndex((localInspection) => inspectionId === localInspection.id);
            if (index < 0) {
                index = state.remoteInspections.findIndex((localInspection) => inspectionId === localInspection.id);
                if (index >= 0) {
                    return state.remoteInspections[index].indoor_flying_audits;
                } else {
                    return undefined;
                }
            } else {
                return state.localInspections[index].outdoor_audits;
            }
        },
    },
    actions: {
        async loadInspections() {
            this.loading = true;
            const response = await axios.get(inspectionsListUrl, {
                params: {
                    recent: true,
                },
            });
            if (response.status === 200) {
                this.remoteInspections = response.data;
            }
            this.loading = false;
            return response;
        },
        async loadCustomerInspections(customerId: number) {
            this.loading = true;
            const response = await axios.get(inspectionsListUrl, {
                params: {
                    costumer: customerId,
                },
            });
            if (response.status === 200) {
                this.remoteInspections = this.remoteInspections.filter(
                    (inspection) => inspection.costumer !== customerId,
                );
                this.remoteInspections.push(...response.data);
            }
            this.loading = false;
            return response;
        },
        async pushData() {
            const response = await axios.post(syncInspectionsUrl, this.localInspections);
            if (response.status === 201) {
                this.localInspections.length = 0;
            }
            return response;
        },
        async syncData() {
            this.loading = true;
            // save different client ids present in localInspections
            const networkStore = useNetworkStore();
            const clientIds = this.localInspections.map((inspection) => inspection.costumer);
            // remove duplicates
            const uniqueClientIds = Array.from(new Set(clientIds));

            try {
                await this.pushData();
                for (const clientId of uniqueClientIds) {
                    await this.loadCustomerInspections(clientId);
                }
                // prevent loading all inspections  with
                // await this.loadInspections();
            } catch (error) {
                console.error("Error syncing inspections:", error);
                // send error to Sentry
                Sentry.captureException(error);
            }
            this.loading = false;
        },
        async saveInspection(inspection: IInspection) {
            this.loading = true;
            const networkStore = useNetworkStore();
            let index = this.localInspections.findIndex((localInspection) => inspection.id === localInspection.id);
            if (index >= 0) {
                this.localInspections[index] = inspection;
            } else {
                index = this.remoteInspections.findIndex((remoteInspection) => inspection.id === remoteInspection.id);
                if (index >= 0) {
                    this.remoteInspections.splice(index, 1);
                }
                this.localInspections.push(inspection);
            }
            if (networkStore.isOnline) {
                const response = await this.pushData();
                if (response.status === 201 || response.status === 200) {
                    if (response.data.created) {
                        this.remoteInspections.push(...(response.data.created as IInspection[]));
                    }
                    if (response.data.updated) {
                        this.remoteInspections.push(...(response.data.updated as IInspection[]));
                    }
                }
            }
            this.loading = false;
        },
        generateBlankInspection(costumer: ICostumer) {
            const now = Date.now();
            const lastCostumerInspection: IInspection = this.getInspectionsByCostumerId(costumer.id).slice(-1)[0];
            const fakeId = -Math.round(now / 1000);
            const inspection = {
                id: fakeId,
                costumer: costumer.id,
                date: new Date().toISOString().split("T")[0],
                repellent_product: "",
                outdoor_audits: [] as IOutdoorTrapAudit[],
                indoor_floor_audits: [] as IIndoorFloorTrapAudit[],
                indoor_flying_audits: [] as IIndoorFlyingTrapAudit[],
            } as IInspection;

            if (lastCostumerInspection) {
                for (const outdoor_audit of lastCostumerInspection.outdoor_audits) {
                    inspection.outdoor_audits.push({
                        uuid: uuidv4(),
                        inspection: fakeId,
                        area: outdoor_audit.area,
                        label: outdoor_audit.label,
                        trap: outdoor_audit.trap,
                        note: "",
                        type: TrapType.outdoor,
                        quantity: OutdoorTrapAuditQuantity.nil,
                        damaged: false,
                    });
                }
                for (const indoor_floor_audit of lastCostumerInspection.indoor_floor_audits) {
                    const capture_limit = useTrapsStore().getTrapById(indoor_floor_audit.trap)
                        ?.capture_limit as IIndoorFloorCaptureLimit;
                    inspection.indoor_floor_audits.push({
                        capture_limit_crawling_lower: capture_limit.crawling_lower,
                        capture_limit_crawling_upper: capture_limit.crawling_upper,
                        capture_limit_rodents_lower: capture_limit.rodents_lower,
                        capture_limit_rodents_upper: capture_limit.rodents_upper,
                        uuid: uuidv4(),
                        inspection: fakeId,
                        area: indoor_floor_audit.area,
                        label: indoor_floor_audit.label,
                        trap: indoor_floor_audit.trap,
                        note: "",
                        type: TrapType.indoorFloor,
                        crawling_amount: 0,
                        rodents_amount: 0,
                    });
                }
                for (const indoor_flying_audit of lastCostumerInspection.indoor_flying_audits) {
                    const capture_limit = useTrapsStore().getTrapById(indoor_flying_audit.trap)
                        ?.capture_limit as IIndoorAirCaptureRangeLimit;
                    inspection.indoor_flying_audits.push({
                        uuid: uuidv4(),
                        inspection: fakeId,
                        area: indoor_flying_audit.area,
                        label: indoor_flying_audit.label,
                        trap: indoor_flying_audit.trap,
                        note: "",
                        type: TrapType.indoorFlying,
                        capture_limit_lower: capture_limit.lower,
                        capture_limit_upper: capture_limit.upper,
                        capture_limit_acceptability_limits: capture_limit.acceptability_limits,
                        quantity: IndoorFlyingTrapAuditQuantity.inferior,
                    });
                }
            }
            return inspection;
        },
        async pushAudit(
            inspectionId: number,
            audit: IOutdoorTrapAudit | IIndoorFloorTrapAudit | IIndoorFlyingTrapAudit,
        ) {
            let index = this.localInspections.findIndex((localInspection) => inspectionId === localInspection.id);
            if (index >= 0 && this.localInspections[index]) {
                const networkStore = useNetworkStore();
                switch (audit.type) {
                    case TrapType.outdoor:
                        this.localInspections[index].outdoor_audits.push(audit);
                        break;
                    case TrapType.indoorFloor:
                        this.localInspections[index].indoor_floor_audits.push(audit);
                        break;
                    case TrapType.indoorFlying:
                        this.localInspections[index].indoor_flying_audits.push(audit);
                        break;
                }
                if (networkStore.isOnline) {
                    await this.pushData();
                }
            } else {
                index = this.remoteInspections.findIndex((localInspection) => inspectionId === localInspection.id);
                if (index >= 0) {
                    switch (audit.type) {
                        case TrapType.outdoor:
                            this.remoteInspections[index].outdoor_audits.push(audit);
                            break;
                        case TrapType.indoorFloor:
                            this.remoteInspections[index].indoor_floor_audits.push(audit);
                            break;
                        case TrapType.indoorFlying:
                            this.remoteInspections[index].indoor_flying_audits.push(audit);
                            break;
                    }
                    await this.saveInspection(this.remoteInspections[index]);
                }
            }
        },
        async removeAudit(inspectionId: number, auditId: string, type: TrapType) {
            let index = this.localInspections.findIndex((localInspection) => inspectionId === localInspection.id);
            if (index >= 0 && this.localInspections[index]) {
                const networkStore = useNetworkStore();
                switch (type) {
                    case TrapType.outdoor: {
                        const auditIndex = this.localInspections[index].outdoor_audits.findIndex(
                            (audit) => audit.uuid === auditId,
                        );
                        if (auditIndex >= 0) {
                            this.localInspections[index].outdoor_audits.splice(auditIndex, 1);
                        }
                        return;
                    }
                    case TrapType.indoorFloor: {
                        const auditIndex = this.localInspections[index].indoor_floor_audits.findIndex(
                            (audit) => audit.uuid === auditId,
                        );
                        if (auditIndex >= 0) {
                            this.localInspections[index].indoor_floor_audits.splice(auditIndex, 1);
                        }
                        return;
                    }
                    case TrapType.indoorFlying: {
                        const auditIndex = this.localInspections[index].indoor_flying_audits.findIndex(
                            (audit) => audit.uuid === auditId,
                        );
                        if (auditIndex >= 0) {
                            this.localInspections[index].indoor_flying_audits.splice(auditIndex, 1);
                        }
                        return;
                    }
                }
                if (networkStore.isOnline) {
                    await this.pushData();
                }
            } else {
                let index = this.remoteInspections.findIndex(
                    (remoteInspection) => inspectionId === remoteInspection.id,
                );
                if (index >= 0) {
                    switch (type) {
                        case TrapType.outdoor: {
                            const auditIndex = this.remoteInspections[index].outdoor_audits.findIndex(
                                (audit) => audit.uuid === auditId,
                            );
                            if (auditIndex >= 0) {
                                this.remoteInspections[index].outdoor_audits.splice(auditIndex, 1);
                            }
                            break;
                        }
                        case TrapType.indoorFloor: {
                            const auditIndex = this.remoteInspections[index].indoor_floor_audits.findIndex(
                                (audit) => audit.uuid === auditId,
                            );
                            if (auditIndex >= 0) {
                                this.remoteInspections[index].indoor_floor_audits.splice(auditIndex, 1);
                            }
                            break;
                        }
                        case TrapType.indoorFlying:
                            const auditIndex = this.remoteInspections[index].indoor_flying_audits.findIndex(
                                (audit) => audit.uuid === auditId,
                            );
                            if (auditIndex >= 0) {
                                this.remoteInspections[index].indoor_flying_audits.splice(auditIndex, 1);
                            }
                            break;
                    }
                    await this.saveInspection(this.remoteInspections[index]);
                }
            }
        },
        /*async setInspectionCostumerSign(inspectionId: number, signImageUrl: string) {
            const index = this.localInspections.findIndex(localInspection => inspectionId === localInspection.id);
            if (index >= 0) {
                const networkStore = useNetworkStore();
                this.localInspections[index].costumer_sign = signImageUrl;
                /!*if (networkStore.isOnline) {
                    await this.pushData();
                }*!/
            } else {
                const index = this.remoteInspections.findIndex(remoteInspection => inspectionId === remoteInspection.id);
                if (index >= 0) {
                    let inspection = this.remoteInspections.splice(index, 1)[0];
                    inspection.costumer_sign = signImageUrl
                    this.localInspections.push(inspection);
                    /!*if (index > 0) {
                        await this.saveInspection(inspection);
                    }*!/
                }
            }
        },*/
        async removeLocalInspection(inspection: IInspection) {
            const index = this.localInspections.findIndex((localInspection) => inspection.id === localInspection.id);
            if (index >= 0) {
                if (inspection.id >= 0) {
                    this.remoteInspections.push(inspection);
                }
                this.localInspections.splice(index, 1);
            }
        },
        async sendInspectionMail(inspectionId: number) {
            const response = await axios.post(inspectionsSendMailUrl(inspectionId));
            return response;
        },
    },
});
